import { useState, useEffect, useRef, useCallback } from "react";
import { ArrowLeft, Pause, X, AlertCircle, FileText, ChevronDown, RefreshCw } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import WorkflowService from "../../services/workflow.service";
import { swr } from "../../utils/swrCache";
import { useOrganizations } from "../../hooks/useOrganizations";
import ExecutionMetrics from "./RunDetail/ExecutionMetrics";
import ExecutionSummary from "./RunDetail/ExecutionSummary";
import ExecutionTimelineView from "./RunDetail/ExecutionTimelineView";
import NotExecutedView from "./RunDetail/NotExecutedView";
import RunMapView from "./RunDetail/RunMapView";

const LOG_LEVELS = ["ALL", "DEBUG", "INFO", "WARNING", "ERROR"];

const LOG_LEVEL_STYLE = {
  debug:   { badge: "bg-zinc-700/60 text-zinc-400",   dot: "bg-zinc-500" },
  info:    { badge: "bg-blue-500/15 text-blue-300",    dot: "bg-blue-400" },
  warning: { badge: "bg-amber-500/15 text-amber-300",  dot: "bg-amber-400" },
  error:   { badge: "bg-rose-500/15 text-rose-300",    dot: "bg-rose-400" },
};

const formatLogTime = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit", fractionalSecondDigits: 3 });
};

const LogLevelBadge = ({ level }) => {
  const key = (level || "").toLowerCase();
  const s = LOG_LEVEL_STYLE[key] || { badge: "bg-zinc-700/60 text-zinc-400", dot: "bg-zinc-500" };
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase shrink-0 ${s.badge}`}>
      {level || "?"}
    </span>
  );
};

const LogsPanel = ({ runId, projectId }) => {
  const { currentAccount } = useOrganizations();
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [levelFilter, setLevelFilter] = useState("ALL");
  const [sourceFilter, setSourceFilter] = useState("");
  const [page, setPage] = useState(0);
  const [levelOpen, setLevelOpen] = useState(false);
  const logsEndRef = useRef(null);
  const LIMIT = 100;

  const load = useCallback(async () => {
    if (!runId || !projectId || !currentAccount?.id) return;
    setLoading(true);
    setError(null);
    try {
      const params = {
        account_id: currentAccount.id,
        project_id: projectId,
        limit: LIMIT,
        offset: page * LIMIT,
        sort_by: "timestamp",
        descending: true,
      };
      if (levelFilter !== "ALL") params.log_level = levelFilter.toLowerCase();
      if (sourceFilter.trim()) params.source = sourceFilter.trim();

      const result = await WorkflowService.getExecutionLogs(runId, params);
      if (result.success) {
        setLogs(result.data.logs || []);
        setTotalCount(result.data.total_count || 0);
      } else {
        setError(result.error || "Failed to load logs");
      }
    } catch (err) {
      setError(err.message || "Failed to load logs");
    } finally {
      setLoading(false);
    }
  }, [runId, projectId, currentAccount?.id, levelFilter, sourceFilter, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(totalCount / LIMIT);

  return (
    <div className="h-full flex flex-col" onClick={() => setLevelOpen(false)}>
      {/* Filters */}
      <div className="flex items-center gap-2 px-6 py-3 border-b border-white/10 shrink-0 flex-wrap">
        {/* Level filter */}
        <div className="relative" onClick={e => e.stopPropagation()}>
          <button
            onClick={() => setLevelOpen(o => !o)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-white/10 text-xs text-gray-300 hover:border-white/20 transition-colors"
          >
            <span className="font-medium">{levelFilter === "ALL" ? "All levels" : levelFilter}</span>
            <ChevronDown className="w-3 h-3 text-gray-500" />
          </button>
          {levelOpen && (
            <div className="absolute left-0 top-full mt-1 w-36 bg-zinc-900 border border-white/10 rounded-lg shadow-2xl overflow-hidden z-20">
              {LOG_LEVELS.map(l => (
                <button
                  key={l}
                  onClick={() => { setLevelFilter(l); setPage(0); setLevelOpen(false); }}
                  className={`w-full px-3 py-2 text-left text-xs transition-colors ${
                    levelFilter === l ? "bg-violet-600/30 text-white" : "text-gray-300 hover:bg-white/5"
                  }`}
                >
                  {l === "ALL" ? "All levels" : l}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Source filter */}
        <input
          type="text"
          value={sourceFilter}
          onChange={e => { setSourceFilter(e.target.value); setPage(0); }}
          placeholder="Filter by source..."
          className="px-2.5 py-1.5 rounded-lg bg-zinc-800 border border-white/10 text-xs text-gray-300 placeholder-gray-600 focus:outline-none focus:border-violet-500/50 w-40 transition-colors"
        />

        <div className="flex-1" />

        <span className="text-[11px] text-gray-600 font-mono">{totalCount} entries</span>

        <button
          onClick={() => load()}
          className="p-1.5 text-gray-500 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Log entries */}
      <div className="flex-1 overflow-auto font-mono text-xs min-h-0">
        {loading ? (
          <div className="flex items-center justify-center h-32 gap-2 text-gray-500">
            <div className="w-4 h-4 border border-violet-500 border-t-transparent rounded-full animate-spin" />
            Loading logs...
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-32 gap-2 text-rose-400">
            <AlertCircle className="w-5 h-5" />
            <span className="text-xs">{error}</span>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 gap-2 text-gray-600">
            <FileText className="w-5 h-5" />
            <span className="text-xs">No logs found</span>
          </div>
        ) : (
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-zinc-950/90 backdrop-blur-sm z-10">
              <tr className="text-[10px] font-sans font-bold text-gray-600 uppercase tracking-widest border-b border-white/6">
                <th className="text-left px-4 py-2 w-32">Time</th>
                <th className="text-left px-2 py-2 w-24">Level</th>
                <th className="text-left px-2 py-2 w-32">Source</th>
                <th className="text-left px-3 py-2">Message</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, idx) => {
                const key = (log.log_level || log.level || "").toLowerCase();
                const rowStyle = key === "error"
                  ? "bg-rose-950/20 hover:bg-rose-950/30"
                  : key === "warning"
                    ? "hover:bg-amber-950/10"
                    : "hover:bg-white/3";
                return (
                  <tr key={log.id || idx} className={`border-b border-white/4 transition-colors ${rowStyle}`}>
                    <td className="px-4 py-2 text-gray-600 text-[10px] whitespace-nowrap">
                      {formatLogTime(log.timestamp)}
                    </td>
                    <td className="px-2 py-2">
                      <LogLevelBadge level={log.log_level || log.level} />
                    </td>
                    <td className="px-2 py-2 text-gray-500 text-[11px] truncate max-w-[120px]" title={log.source}>
                      {log.source || "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-200 text-[11px] whitespace-pre-wrap break-words leading-relaxed">
                      {log.message}
                      {log.data && Object.keys(log.data).length > 0 && (
                        <details className="mt-1">
                          <summary className="text-gray-600 cursor-pointer select-none text-[10px] hover:text-gray-400">
                            metadata
                          </summary>
                          <pre className="mt-1 text-[10px] text-gray-500 bg-black/30 rounded p-2 overflow-auto">
                            {JSON.stringify(log.data, null, 2)}
                          </pre>
                        </details>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div ref={logsEndRef} />
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-5 py-2.5 border-t border-white/8 shrink-0">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-3 py-1 text-xs rounded-lg border border-white/10 text-gray-400 hover:text-white hover:border-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          <span className="text-[11px] text-gray-600">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="px-3 py-1 text-xs rounded-lg border border-white/10 text-gray-400 hover:text-white hover:border-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default function RunInspector({ runId, onClose, projectId }) {
  const { currentAccount } = useOrganizations();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("map");
  const [timeline, setTimeline] = useState(null);
  const [timelineError, setTimelineError] = useState(null);
  const [graph, setGraph] = useState(null);
  const [graphError, setGraphError] = useState(null);
  const eventSourceRef = useRef(null);

  useEffect(() => {
    if (!runId || !projectId || !currentAccount?.id) return;

    // De la ejecución solo se lee la cabecera. Lo que pasó adentro lo cuenta
    // el timeline, que es la única fuente con una entrada por corrida: armarlo
    // desde `step_progress` obligaba a repartir la duración total en partes
    // iguales entre los pasos, que es una duración inventada.
    const toRunHeader = (executionData) => {
      const safeName = (value) => {
        if (!value) return 'Unknown';
        if (typeof value === 'string') return value;
        if (typeof value === 'object' && value.name) return value.name;
        return String(value);
      };

      return {
        id: executionData.execution_id || executionData.id,
        workflow_name: safeName(executionData.workflow_name || 'Unknown Workflow'),
        status: executionData.status,
        trigger_type: safeName(executionData.trigger_type || 'manual'),
        started_at_str: executionData.started_at || null,
      };
    };

    // Initial fetch
    const fetchInitialRun = async () => {
      try {
        const result = await WorkflowService.getExecution(runId, {
          project_id: projectId,
          include_step_results: true,
          include_logs_summary: true,
        });

        if (result.success) {
          setRun(toRunHeader(result.data.execution));
        } else {
          setError(result.error || 'Failed to load execution details');
        }
      } catch (err) {
        console.error("Failed to fetch run:", err);
        setError(err.message || 'Failed to load execution details');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialRun();

    // Set up SSE connection for real-time updates using fetch with authentication headers
    const streamController = WorkflowService.streamExecutionUpdates(
      runId,
      projectId,
      (data) => {
        // Handle incoming SSE message
        console.log('SSE event received:', data);
        
        // Handle different event types
        if (data.event_type === 'heartbeat') {
          // Heartbeat events just confirm connection is alive
          return;
        }
        
        if (data.execution) {
          // Full execution update
          const header = toRunHeader(data.execution);
          setRun(header);

          // Close SSE connection if execution is finished
          if (['completed', 'failed', 'cancelled'].includes(header.status)) {
            if (eventSourceRef.current) {
              eventSourceRef.current.abort();
              eventSourceRef.current = null;
            }
          }
        } else if (data.status || data.execution_id) {
          // Partial status update — preserve steps array to avoid undefined.length crash
          setRun(prev => prev ? ({
            ...prev,
            status: data.status || prev.status,
            updatedAt: data.timestamp || new Date().toISOString(),
          }) : prev);
        }
      },
      (err) => {
        // Handle stream error
        console.error('SSE connection error:', err);
        if (eventSourceRef.current) {
          eventSourceRef.current.abort();
          eventSourceRef.current = null;
        }
        // Could fallback to polling here if needed
      }
    );
    
    // Store the controller reference for cleanup
    streamController.then(controller => {
      eventSourceRef.current = controller;
    });
    
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.abort();
        eventSourceRef.current = null;
      }
    };
  }, [runId, projectId, currentAccount?.id]);

  // La historia real de la corrida: una entrada por corrida de paso, con las
  // esperas intercaladas. Se vuelve a pedir cuando cambia el estado de la
  // ejecución, porque mientras corre se le siguen agregando entradas.
  const runStatus = run?.status;
  useEffect(() => {
    if (!runId || !projectId) return undefined;
    let cancelled = false;

    // La clave lleva el estado: mientras la ejecución avanza se vuelve a pedir,
    // y una vez terminada queda cacheada y cambiar de pestaña no cuesta nada.
    swr(
      `timeline:${runId}:${runStatus || "unknown"}`,
      async () => {
        const result = await WorkflowService.getExecutionTimeline(runId, {
          project_id: projectId,
          limit: 1000,
        });
        if (!result.success) throw new Error(result.error || "Failed to load the timeline");
        return result.data;
      },
      { maxAgeMs: 15000 }
    )
      .then((data) => {
        if (cancelled) return;
        setTimeline(data);
        setTimelineError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setTimelineError(err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [runId, projectId, runStatus]);

  // El grafo de la versión que corrió es inmutable: se pide una vez y no se
  // vuelve a pedir aunque la ejecución avance.
  useEffect(() => {
    if (!runId || !projectId) return undefined;
    let cancelled = false;

    swr(
      `graph:${runId}`,
      async () => {
        const result = await WorkflowService.getExecutionGraph(runId, { project_id: projectId });
        if (!result.success) throw new Error(result.error || "Failed to load the graph");
        return result.data;
      },
      // El grafo de la versión ejecutada no cambia nunca.
      { maxAgeMs: Infinity }
    )
      .then((data) => {
        if (cancelled) return;
        setGraph(data);
        setGraphError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setGraphError(err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [runId, projectId]);

  const handleCancel = async () => {
    if (!runId || !projectId || !currentAccount?.id) return;

    try {
      const result = await WorkflowService.cancelExecution(runId, {
        account_id: currentAccount.id,
        project_id: projectId,
        reason: 'Cancelled by user'
      });

      if (result.success) {
        // 202 async ack — optimistically show 'cancelling'; SSE stream will
        // deliver the terminal 'cancelled' update when the backend finishes draining.
        setRun(prev => prev ? { ...prev, status: 'cancelling' } : prev);
      } else {
        setError(result.error || 'Failed to cancel execution');
      }
    } catch (err) {
      console.error('Failed to cancel execution:', err);
      setError('Failed to cancel execution');
    }
  };

  if (loading && !run) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          <div className="text-sm text-gray-500 font-medium">Loading execution details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-3">
          <AlertCircle className="w-8 h-8 text-red-400" />
          <div className="text-sm text-red-400 font-medium">Failed to load execution</div>
          <p className="text-xs text-gray-500">{error}</p>
          <Button onClick={onClose} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  if (!run) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm text-gray-500">Execution not found</div>
      </div>
    );
  }

  // El costo sale de las filas por corrida, que es donde queda registrado —
  // incluso en una ejecución cancelada.
  const timelineCoins = (timeline?.timeline || []).reduce(
    (total, entry) => total + (entry.cost?.coins || 0),
    0
  );

  const shortRunId = runId ? `${runId.substring(0, 4)}...${runId.substring(runId.length - 4)}` : "—";
  const statusColors = {
    running:    "bg-blue-500/20 text-blue-400 border-blue-500/40",
    cancelling: "bg-amber-500/20 text-amber-400 border-amber-500/40",
    paused:     "bg-yellow-500/20 text-yellow-400 border-yellow-500/40",
    waiting:    "bg-yellow-500/20 text-yellow-400 border-yellow-500/40",
    completed:  "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
    failed:     "bg-rose-500/20 text-rose-400 border-rose-500/40",
    cancelled:  "bg-zinc-700/40 text-zinc-400 border-zinc-600/40",
    expired:    "bg-zinc-800 text-zinc-500 border-zinc-700/40",
  };
  const statusClass = statusColors[run.status?.toLowerCase()] || "bg-zinc-500/20 text-zinc-400 border-zinc-500/40";

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-6 py-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1.5 hover:bg-white/5 rounded-lg"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
              <span className="hover:text-gray-300 cursor-pointer" onClick={onClose}>Runs</span>
              <span className="text-gray-700">/</span>
              <span className="font-mono text-gray-400">{shortRunId}</span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <h2 className="text-sm font-semibold text-white">{run.workflow_name}</h2>
              <Badge variant="outline" className={`text-[10px] border font-semibold ${statusClass}`}>
                {run.status}
              </Badge>
              {run.trigger_type && (
                <Badge variant="outline" className="text-[9px] border bg-zinc-800 text-zinc-300 border-zinc-700">
                  {run.trigger_type}
                </Badge>
              )}
              {run.started_at_str && (
                <span className="text-[10px] text-gray-500">
                  · Started {new Date(run.started_at_str).toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {(run.status === "running" || run.status === "cancelling" || run.status === "paused" || run.status === "waiting") && (
            <div className="flex gap-2 mr-2">
              {run.status === "running" && (
                <Button variant="outline" size="sm" className="text-xs bg-black/40 backdrop-blur-xl border-white/10 hover:border-violet-500/30 hover:bg-white/5 font-semibold">
                  <Pause className="w-3 h-3 mr-1.5" />
                  Pause
                </Button>
              )}
              {run.status === "cancelling" ? (
                <Button variant="outline" size="sm" disabled className="text-xs text-amber-400 bg-black/40 backdrop-blur-xl border-amber-500/40 opacity-70 font-semibold cursor-not-allowed">
                  <span className="w-3 h-3 mr-1.5 rounded-full border-2 border-amber-400 border-t-transparent animate-spin inline-block" />
                  Cancelling…
                </Button>
              ) : (
                <Button variant="outline" size="sm" className="text-xs text-red-400 bg-black/40 backdrop-blur-xl border-red-500/40 hover:bg-red-500/10 font-semibold" onClick={handleCancel}>
                  <X className="w-3 h-3 mr-1.5" />
                  Cancel
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Los contadores describen la corrida entera, así que viven arriba de las
          pestañas y no adentro de una: cambiar de pestaña no cambia cuánto
          tardó ni cuánto costó. */}
      {timeline && (
        <div className="px-6 pt-4 shrink-0">
          <ExecutionMetrics
            counters={timeline.counters}
            duration={timeline.duration}
            coins={timelineCoins}
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 px-6 border-b border-white/10 shrink-0">
        <button
          onClick={() => setActiveTab("map")}
          className={`px-3 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
            activeTab === "map"
              ? "border-violet-500 text-white"
              : "border-transparent text-gray-500 hover:text-gray-300"
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab("summary")}
          className={`px-3 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
            activeTab === "summary"
              ? "border-violet-500 text-white"
              : "border-transparent text-gray-500 hover:text-gray-300"
          }`}
        >
          Summary
        </button>
        <button
          onClick={() => setActiveTab("timeline")}
          className={`px-3 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
            activeTab === "timeline"
              ? "border-violet-500 text-white"
              : "border-transparent text-gray-500 hover:text-gray-300"
          }`}
        >
          Timeline
        </button>
        <button
          onClick={() => setActiveTab("skipped")}
          className={`px-3 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
            activeTab === "skipped"
              ? "border-violet-500 text-white"
              : "border-transparent text-gray-500 hover:text-gray-300"
          }`}
        >
          Not executed
          {timeline?.not_executed?.length > 0 && (
            <span className="ml-1.5 text-gray-600">{timeline.not_executed.length}</span>
          )}
        </button>
        <button
          onClick={() => setActiveTab("logs")}
          className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
            activeTab === "logs"
              ? "border-violet-500 text-white"
              : "border-transparent text-gray-500 hover:text-gray-300"
          }`}
        >
          <FileText className="w-3 h-3" />
          Logs
        </button>
      </div>

      {activeTab === "summary" && (
        <div className="flex-1 min-h-0 overflow-auto p-6 space-y-4">
          {timelineError ? (
            <div className="flex h-40 flex-col items-center justify-center gap-2">
              <AlertCircle className="h-6 w-6 text-red-400" />
              <div className="text-xs text-red-400">Failed to load the execution timeline</div>
              <p className="text-[10px] text-gray-500">{timelineError}</p>
            </div>
          ) : !timeline ? (
            <div className="flex h-40 items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
            </div>
          ) : (
            <ExecutionSummary
              timeline={timeline.timeline}
              degraded={timeline.degraded}
              degradedReason={timeline.degraded_reason}
              truncated={timeline.truncated}
              truncatedReason={timeline.truncated_reason}
            />
          )}
        </div>
      )}

      {activeTab === "timeline" && (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-6">
          {timelineError ? (
            <div className="flex h-40 flex-col items-center justify-center gap-2">
              <AlertCircle className="h-6 w-6 text-red-400" />
              <div className="text-xs text-red-400">Failed to load the execution timeline</div>
              <p className="text-[10px] text-gray-500">{timelineError}</p>
            </div>
          ) : !timeline ? (
            <div className="flex h-40 items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
            </div>
          ) : (
            <ExecutionTimelineView
              runId={runId}
              projectId={projectId}
              timeline={timeline.timeline}
              degraded={timeline.degraded}
              degradedReason={timeline.degraded_reason}
              truncated={timeline.truncated}
              truncatedReason={timeline.truncated_reason}
            />
          )}
        </div>
      )}

      {activeTab === "skipped" && (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-6">
          {!timeline ? (
            <div className="flex h-40 items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
            </div>
          ) : (
            <NotExecutedView notExecuted={timeline.not_executed} />
          )}
        </div>
      )}

      {activeTab === "map" && (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-6">
          {!timeline && !graphError ? (
            <div className="flex h-40 items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-violet-500 border-t-transparent" />
            </div>
          ) : (
            <RunMapView
              graph={graph}
              graphError={graphError}
              timeline={timeline?.timeline}
              notExecuted={timeline?.not_executed}
            />
          )}
        </div>
      )}

      {activeTab === "logs" && (
        <div className="flex-1 min-h-0 overflow-hidden">
          <LogsPanel runId={runId} projectId={projectId} />
        </div>
      )}
    </div>
  );
}
